import { ChangeDetectionStrategy, Component, WritableSignal, computed, effect, input, output, signal } from '@angular/core';

import { DsButtonIcon } from '@frontend/ui/button-icon';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { SliderComponent } from '../../../common/slider/slider.component';
import { SliderGradientDirective } from './market-slider-gradient.directive';

@Component({
    selector: 'ms-market-slider',
    imports: [SliderComponent, DsButtonIcon, IconCustomComponent, SliderGradientDirective],
    templateUrl: './market-slider.component.html',
    styleUrl: './market-slider.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketSliderComponent {
    readonly steps = input.required<number[]>();
    readonly selectedValue = input<number | undefined>(undefined);
    readonly centerValue = input<number | undefined>(undefined);
    readonly rightLabel = input<string | undefined>(undefined);
    readonly leftLabel = input<string | undefined>(undefined);
    readonly isDisabled = input<boolean>(false);
    readonly identityKey = input<string>();
    selectedValueChange = output<number>();

    readonly sliderValue: WritableSignal<number> = signal(0);

    readonly inputValue = computed(() => {
        const steps = this.steps();
        const selected = this.selectedValue();
        const center = this.centerValue();

        if (!steps.length) {
            return 0;
        }

        if (selected !== undefined) {
            return selected;
        }

        if (center !== undefined && steps.includes(center)) {
            return center;
        }

        return this.getMiddleValue(steps);
    });

    readonly isMinValue = computed(() => this.sliderValue() === this.steps()[0]);
    readonly isMaxValue = computed(() => this.sliderValue() === this.steps()[this.steps().length - 1]);

    constructor() {
        effect(() => {
            this.identityKey();
            const initial = this.inputValue();
            this.sliderValue.set(initial);
        });

        effect(() => {
            const val = this.sliderValue();
            this.selectedValueChange.emit(val);
        });
    }

    incrementOrDecrementPoints(isIncrement: boolean = false): void {
        const steps = this.steps();
        const current = this.sliderValue();
        const currentIndex = steps.indexOf(current);

        let newIndex = currentIndex;
        if (isIncrement && currentIndex < steps.length - 1) {
            newIndex++;
        } else if (!isIncrement && currentIndex > 0) {
            newIndex--;
        }

        if (newIndex !== currentIndex) {
            this.sliderValue.set(steps[newIndex]);
        }
    }

    onValueChange(value: number): void {
        this.sliderValue.set(value);
    }

    private getMiddleValue(arr: number[]): number {
        const sorted = [...arr].sort((a, b) => a - b);
        return sorted[Math.floor(sorted.length / 2)];
    }
}
